import React, { useEffect, useState } from "react";
import { Button } from "../../Components/Button";
import { useTranslation } from "react-i18next";
import FormLabel from "../../Components/FormLabel";
import {
  clearUpdateServiceAddOn,
  clearUpdateServiceAddOnError,
  requestCreateServiceAddOn,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { NumericFormat } from "react-number-format";
import { classNames, currencyFormat } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import InputSkeleton from "../../Components/InputSkeleton";
import { ModalConfirm } from "../../Components/ModalConfirm";

const minPrice = 1000;
const maxPrice = 50000000;

const BusinessServiceAddonCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const businessServiceDispatch = useBusinessServiceDispatch();
  const { newServiceAddOn, newServiceAddOnError, newServiceAddOnLoading } =
    useBusinessServiceState();

  const [fieldError, setfieldError] = useState(false);

  const [addOnName, setaddOnName] = useState("");
  const [description, setdescription] = useState("");
  const [price, setprice] = useState("");
  const [basePrice, setbasePrice] = useState("");

  const [addOnNameError, setaddOnNameError] = useState(null);
  const [priceError, setpriceError] = useState(null);
  const [basePriceError, setbasePriceError] = useState(null);

  const [submitted, setsubmitted] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);

  useEffect(() => {
    return () => {
      clearUpdateServiceAddOn(businessServiceDispatch);
      clearUpdateServiceAddOnError(businessServiceDispatch);
    };
  }, [businessServiceDispatch]);

  useEffect(() => {
    if (submitted) {
      const isaddOnNameValid = addOnName.length >= 10;
      const isPriceValid = price >= 0 && price >= minPrice && price <= maxPrice;
      const isBasePriceValid = basePrice > 0 ? basePrice > Number(price) : true;

      if (isaddOnNameValid) {
        setErrorMessage("addOnName", "");
      } else {
        if (addOnName.length === 0) {
          setErrorMessage("addOnName", "addOnNameRequired");
        } else {
          setErrorMessage("addOnName", "minimumAddOnName");
        }
      }

      if (isPriceValid) {
        setErrorMessage("price", "");
      } else {
        if (price < minPrice) {
          setErrorMessage("price", "minimumPriceIs");
        } else if (price > maxPrice) {
          setErrorMessage("price", "maximumPriceIs");
        } else {
          setErrorMessage(
            "price",
            "Price is required and must be greater than 0."
          );
        }
      }

      if (isBasePriceValid) {
        setErrorMessage("basePrice", "");
      } else {
        setErrorMessage("basePrice", "basePriceRequired");
      }
    }
  }, [addOnName, price, basePrice, description, submitted]);

  useEffect(() => {
    if (newServiceAddOn?.id) {
      setshowSuccessModal(true);
    }
  }, [newServiceAddOn?.id]);

  const validateInput = () => {
    let currentValidaddOnName = true;
    let currentValidPrice = true;
    let currentValidBasePrice = true;

    const isaddOnNameValid = addOnName.length >= 10;
    const isPriceValid = price >= 0 && price >= minPrice && price <= maxPrice;
    const isBasePriceValid = basePrice > 0 ? basePrice > Number(price) : true;

    currentValidaddOnName = isaddOnNameValid;
    currentValidPrice = isPriceValid;
    currentValidBasePrice = isBasePriceValid;

    return currentValidaddOnName && currentValidPrice && currentValidBasePrice;
  };

  const setErrorMessage = (field, message, index) => {
    switch (field) {
      case "addOnName":
        setaddOnNameError(message);
        break;
      case "price":
        setpriceError(message);
        break;
      case "basePrice":
        setbasePriceError(message);
        break;
      default:
        break;
    }
  };

  const onCreate = () => {
    setsubmitted(true);
    const valid = validateInput();

    if (valid) {
      setfieldError(false);
      const input = {
        name: addOnName,
        description,
        price,
      };

      if (basePrice) {
        input.basePrice = basePrice;
      }

      requestCreateServiceAddOn(businessServiceDispatch, {
        input,
      });
    } else {
      setfieldError(true);
    }
  };

  const navigateToAddOnList = () => {
    setshowSuccessModal(false);
    if (!newServiceAddOnError) {
      navigate(`/manage-business-service/addons`);
      clearUpdateServiceAddOn(businessServiceDispatch);
    }
  };

  return (
    <div>
      <div className="max-w-4xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-4">
        <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow-sm sm:overflow-hidden rounded-md sm:rounded-lg lg:col-start-1 lg:col-span-2">
          <div className="space-y-6 px-4 py-6 sm:p-6">
            {fieldError && (
              <Alert
                title="Incomplete Form"
                type="warning"
                message="Please fill out all fields."
              />
            )}
            {newServiceAddOnError && (
              <Alert
                type="error"
                title="Error"
                message={
                  newServiceAddOnError?.message || "Error Update Service AddOn"
                }
                errors={newServiceAddOnError?.errors}
              />
            )}

            {newServiceAddOn?.id && (
              <ModalConfirm
                open={showSuccessModal}
                setOpen={(open) => {
                  setshowSuccessModal(open);
                }}
                title="Success"
                description="serviceAddOnSuccessCreated"
                type="SUCCESS"
                onConfirm={navigateToAddOnList}
              />
            )}
            <div className="grid grid-cols-1 gap-4">
              {newServiceAddOnLoading ? (
                <div className="flex flex-col gap-4">
                  <InputSkeleton rows={2} />
                  <div className="flex-1 space-y-2 animate-pulse">
                    <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
                    <div className="block h-44 bg-gray-300 rounded-md"></div>
                  </div>
                  <InputSkeleton rows={2} />
                  <div className="flex-1 space-y-2 animate-pulse">
                    <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
                    <div className="block h-44 bg-gray-300 rounded-md"></div>
                  </div>
                </div>
              ) : (
                <div className="col-span-6 sm:col-span-3 space-y-6">
                  <div>
                    <FormLabel
                      htmlFor="addOnName"
                      label={t("addOnName")}
                      required
                    />
                    <input
                      type="text"
                      name="addOnName"
                      id="addOnName"
                      value={addOnName}
                      className={classNames(
                        addOnNameError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                      placeholder={t("enterAddOnName")}
                      onChange={(e) => setaddOnName(e.target.value)}
                      maxLength={120}
                      minLength={10}
                    />
                    {addOnNameError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(addOnNameError)}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel htmlFor="description" label={t("Description")} />
                    <textarea
                      name="description"
                      id="description"
                      value={description}
                      className="border-gray-300 focus:border-blue-500 focus:ring-blue-500
                        mt-1 block w-full text-sm rounded-md shadow-sm resize-none"
                      placeholder={t("enterAddOnDescription")}
                      onChange={(e) => setdescription(e.target.value)}
                      rows={4}
                      maxLength={480}
                      minLength={10}
                    />
                  </div>
                  <div>
                    <FormLabel htmlFor="price" label={t("Price")} required />
                    <NumericFormat
                      type="text"
                      name="price"
                      id="price"
                      className={classNames(
                        priceError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                      placeholder={t("Enter Price")}
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="input"
                      prefix={"Rp"}
                      value={price}
                      onValueChange={(value) => {
                        setprice(value.value);
                        if (value.value < minPrice) {
                          setErrorMessage("price", "minimumPriceIs");
                        } else if (value.value > maxPrice) {
                          setErrorMessage("price", "maximumPriceIs");
                        } else {
                          setErrorMessage("price", "");
                        }
                      }}
                    />
                    {priceError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(priceError, {
                          minimumPrice: currencyFormat(minPrice),
                          maximumPrice: currencyFormat(maxPrice),
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel htmlFor="basePrice" label={t("basePrice")} />
                    <NumericFormat
                      type="text"
                      name="basePrice"
                      id="basePrice"
                      className={classNames(
                        basePriceError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                      placeholder={t("enterBasePrice")}
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="input"
                      prefix={"Rp"}
                      value={basePrice}
                      onValueChange={(value) => {
                        setbasePrice(value.value);
                        if (value.value > 0 && value.value < Number(price)) {
                          setErrorMessage("basePrice", "basePriceRequired");
                        } else {
                          setErrorMessage("basePrice", "");
                        }
                      }}
                    />
                    {basePriceError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(basePriceError)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
            <Button
              className="w-full sm:w-auto"
              loading={newServiceAddOnLoading}
              disabled={newServiceAddOnLoading}
              onClick={onCreate}
              title="Create"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessServiceAddonCreate;
